import { useEffect, useRef, useState } from 'react'
import { Sheet } from '../ui/Sheet'
import { Icon } from '../ui/Icon'
import { deleteAccount, fetchSession, type DeleteOutcome, type Session } from './googleSession'

/**
 * The way OUT for good — guideline 5.1.1(v): an app that sets up an account must
 * let a person start deleting it from inside the app, and Apple names the things
 * that do not count ("make a phone call, send an email, or go through other
 * support flows"). The privacy policy offered email; this is the real door.
 *
 * Quiet on the page, loud in the sheet. The sheet says exactly what goes (machines
 * unpaired, projects, messages, notes, files, the subscription record, every
 * session), that a STORE subscription keeps renewing until cancelled in the store,
 * and asks for the word DELETE to be typed before the button arms. Like signing out,
 * success RELOADS: this app is full of state fetched under the account that no
 * longer exists.
 *
 * NO TAP OPENS IT — owner, 2026-09-13: 把 delete account 的行為改為使用者從左向右滑動才會
 * 跳出接下來的選單, 這樣子會更安全. It was a text line one stray thumb could hit, at the
 * foot of a panel people scroll through. Now the gesture itself is the first gate:
 * drag the knob the width of the track, and only then does the sheet (and its typed
 * DELETE) appear. Two deliberate acts, neither of which a pocket can perform.
 */
export function DeleteAccountButton() {
  const [session, setSession] = useState<Session | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    void fetchSession().then(setSession)
  }, [])

  if (session?.kind !== 'in') return null
  return (
    <>
      <SlideToDelete onComplete={() => setOpen(true)} />
      {open && <DeleteAccountSheet email={session.identity.email} onClose={() => setOpen(false)} />}
    </>
  )
}

/** Keyboard steps to cross the track — four presses, so it is as deliberate as the drag. */
const KEY_STEP = 0.25

/**
 * Slide left → right to open the delete sheet.
 *
 * A POINTER DRAG THAT STARTS ON THE KNOB, not a tap anywhere on the track. That
 * distinction is the whole safety property, and it is why this is not an
 * `<input type="range">`: a range input jumps its thumb to wherever the track is
 * tapped, so a single tap near the right edge would complete the gesture — the
 * opposite of what was asked for.
 *
 * `touch-action: pan-y` on the knob (CSS): the panel this sits in scrolls
 * vertically, and a control that swallowed vertical drags would trap a scrolling
 * thumb at the very bottom of the menu.
 *
 * Keyboard reaches it too, and is held to the same deliberateness: it is a slider,
 * four ArrowRight presses cross it, and Enter/Space do NOTHING — a single keypress
 * opening the delete sheet would be the tap this replaced.
 */
function SlideToDelete({ onComplete }: { onComplete: () => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const knobRef = useRef<HTMLDivElement>(null)
  /** 0 at rest, 1 at the far end. The knob's position is derived from it. */
  const [frac, setFrac] = useState(0)
  const [dragging, setDragging] = useState(false)
  /** Pointer x at grab, minus the knob offset it started from — set on pointerdown. */
  const origin = useRef(0)

  /**
   * How far the knob can travel, in px — measured at every use rather than cached,
   * because this panel resizes (the rail docks, the phone rotates) and a stale
   * number would leave the knob either short of the end or past it.
   */
  const travel = () => {
    const t = trackRef.current?.getBoundingClientRect().width ?? 0
    const k = knobRef.current?.getBoundingClientRect().width ?? 0
    return Math.max(1, t - k - 6) // 6 = the track's 3px padding, both sides
  }

  /** Back to rest either way: the control must never be left armed behind the sheet,
   *  so closing it and opening it again costs the same gesture. */
  const finish = (done: boolean) => {
    setFrac(0)
    if (done) onComplete()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = Math.min(1, frac + KEY_STEP)
      if (next >= 1) finish(true)
      else setFrac(next)
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'Home' || e.key === 'Escape') {
      e.preventDefault()
      setFrac(0)
    } else if (e.key === 'Enter' || e.key === ' ') {
      // Deliberately inert. See the note above.
      e.preventDefault()
    }
  }

  const pct = Math.round(frac * 100)
  return (
    <div className={`del-slide${dragging ? ' dragging' : ''}${frac > 0 ? ' moved' : ''}`} ref={trackRef}>
      <span className="del-slide-label" aria-hidden="true">
        Slide to delete account
      </span>
      <div
        ref={knobRef}
        className="del-knob"
        role="slider"
        tabIndex={0}
        aria-label="Slide to delete account"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={pct === 0 ? 'Slide right to delete account' : `${pct}% of the way`}
        style={{ transform: `translateX(${frac * travel()}px)` }}
        onKeyDown={onKeyDown}
        onPointerDown={(e) => {
          origin.current = e.clientX - frac * travel()
          setDragging(true)
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            /* older engines: the drag ends when the finger leaves the knob */
          }
        }}
        onPointerMove={(e) => {
          if (!dragging) return
          const max = travel()
          setFrac(Math.min(1, Math.max(0, (e.clientX - origin.current) / max)))
        }}
        onPointerUp={(e) => {
          if (!dragging) return
          setDragging(false)
          const reached = (e.clientX - origin.current) / travel()
          // 0.995, not 1: the last pixel of a track is not a target anyone can hit
          // deliberately, and asking for it would turn a completed swipe into a
          // silent no-op.
          finish(reached >= 0.995)
        }}
        /* The gesture that ends without a release — a system swipe taking over, the
           finger leaving the digitiser. It must reset, never complete: #38 in this
           app's own record is a pointercancel that shared a finish handler and sent
           what the user had not asked for. */
        onPointerCancel={() => {
          setDragging(false)
          setFrac(0)
        }}
      >
        {/* No `label`, so the icon is aria-hidden: the slider's own name is the
            accessible one, and a second would read it twice. */}
        <Icon name="chevron_right" size={16} />
      </div>
    </div>
  )
}

const WORD = 'DELETE'

/** Where a store subscription is cancelled — the sentence the 409 needs. */
function storePlace(store: string): { where: string; url: string | null } {
  switch (store) {
    case 'app_store':
      return { where: 'Settings › Apple Account › Subscriptions', url: 'https://apps.apple.com/account/subscriptions' }
    case 'play_store':
      return { where: 'Google Play › Subscriptions', url: 'https://play.google.com/store/account/subscriptions' }
    default:
      return { where: 'the store you bought it in', url: null }
  }
}

function DeleteAccountSheet({ email, onClose }: { email: string; onClose: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<DeleteOutcome | null>(null)
  const armed = typed.trim() === WORD && !busy

  const go = async () => {
    setBusy(true)
    setOutcome(null)
    const r = await deleteAccount(typed.trim())
    if (r.ok) {
      // Gone. Same rule as Sign out: a reload is the only version with no residue.
      window.location.replace('/')
      return
    }
    setBusy(false)
    setOutcome(r)
  }

  return (
    <Sheet
      title="Delete account"
      onClose={onClose}
      className="del-sheet"
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onClose} disabled={busy}>
            Keep my account
          </button>
          <button type="button" className="sheet-btn danger" onClick={() => void go()} disabled={!armed}>
            {busy ? 'Deleting…' : 'Delete account'}
          </button>
        </>
      }
    >
      <p className="del-what">
        This deletes the Avengents account <b>{email}</b> and everything in it: your paired machines are
        unpaired, your projects, messages, notes and attached files are removed, your subscription record
        is deleted, and you are signed out on every device. It cannot be undone.
      </p>
      <p className="del-sub">
        A subscription bought through the App Store or Google Play keeps renewing until you cancel it
        there — cancel it first. A subscription bought on the web is cancelled for you.
      </p>
      <label className="del-confirm">
        <span>Type {WORD} to confirm</span>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={busy}
          aria-label={`Type ${WORD} to confirm`}
        />
      </label>
      {outcome && !outcome.ok && outcome.reason === 'active_subscription' && (
        <p className="gh-note del-note" role="alert">
          Your subscription is still renewing. Cancel it in {storePlace(outcome.store).where} first, then delete
          your account.{' '}
          {storePlace(outcome.store).url && (
            <a href={storePlace(outcome.store).url ?? undefined} target="_blank" rel="noopener noreferrer">
              Open subscriptions
            </a>
          )}
        </p>
      )}
      {outcome && !outcome.ok && outcome.reason === 'failed' && (
        <p className="gh-note del-note" role="alert">
          Could not delete the account: {outcome.detail}. Try again, or write to avengents.ai@gmail.com.
        </p>
      )}
    </Sheet>
  )
}
